import React from "react";
import { Calendar, Clock } from "lucide-react";

function shouldShowClassName(title, cardTitle) {
  if (!title) return false;
  if (!cardTitle) return true;
  return title.trim().toLowerCase() !== cardTitle.trim().toLowerCase();
}

function isRequestLine(line) {
  return line?.variant === "request";
}

/**
 * Standard class schedule list.
 * @param {number|null} maxItems - Cap of non-request class rows (listing cards); omit for full list
 * @param {boolean} compact - Tighter padding for cards; hides on-request row (already on card UI)
 */
export default function TeacherScheduleBlock({
  lines = [],
  fallbackSchedule = "",
  cardTitle = "",
  className = "",
  maxItems = null,
  compact = false,
}) {
  const raw =
    lines.length > 0
      ? lines
      : fallbackSchedule
        ? [{ title: "", when: fallbackSchedule, schedule: fallbackSchedule, price: "" }]
        : [];

  if (raw.length === 0) return null;

  const requestLines = raw.filter(isRequestLine);
  const classLines = raw.filter((line) => !isRequestLine(line));
  const limit =
    maxItems != null && Number.isFinite(Number(maxItems)) ? Math.max(0, Number(maxItems)) : null;
  const visibleClasses = limit != null ? classLines.slice(0, limit) : classLines;
  const moreCount = limit != null ? Math.max(0, classLines.length - visibleClasses.length) : 0;
  // On compact cards, drop the request row — "Price & schedule on request" already shows above.
  const items = compact ? visibleClasses : [...visibleClasses, ...requestLines];

  if (items.length === 0 && moreCount === 0) return null;

  const pad = compact ? "px-3 py-2.5" : "px-4 py-3.5";
  const headerPad = compact ? "px-3 py-2" : "px-4 py-3";

  return (
    <div
      className={`rounded-xl border border-gray-200 bg-white overflow-hidden shadow-sm ${className}`}
    >
      <div className={`${headerPad} border-b border-gray-100 bg-gray-50`}>
        <p className="text-xs font-semibold uppercase tracking-wider text-gray-600 flex items-center gap-2">
          <Calendar size={14} className="text-gray-500" />
          Class schedule
        </p>
      </div>

      <ul className="divide-y divide-gray-100" role="list">
        {items.map((line, index) => {
          const isRequest = isRequestLine(line);
          const showName = !isRequest && shouldShowClassName(line.title, cardTitle);
          const when = line.when || line.schedule || "";
          const price = line.price || "";

          if (isRequest) {
            return (
              <li
                key={`request-${index}`}
                className={`${pad} bg-gray-50/80 flex items-center gap-3`}
              >
                <span
                  className="flex h-7 w-7 shrink-0 items-center justify-center rounded-full bg-white border border-gray-200 text-xs font-semibold text-gray-500"
                  aria-hidden
                >
                  +
                </span>
                <p className="text-sm font-bold text-gray-900">{when}</p>
              </li>
            );
          }

          return (
            <li
              key={`${line.title}-${when}-${index}`}
              className={`${pad} hover:bg-gray-50/60 transition-colors`}
            >
              <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-1.5 sm:gap-3">
                <div className="min-w-0 flex-1">
                  {showName ? (
                    <p className="text-sm font-semibold text-gray-900 leading-snug line-clamp-1">
                      {line.title}
                    </p>
                  ) : null}
                  <p
                    className={`text-sm text-gray-600 flex items-start gap-2 leading-snug ${
                      showName ? "mt-0.5" : ""
                    }`}
                  >
                    <Clock size={14} className="text-gray-400 mt-0.5 shrink-0" aria-hidden />
                    <span className="line-clamp-2">{when}</span>
                  </p>
                </div>
                {price ? (
                  <p className="shrink-0 text-sm font-semibold tabular-nums text-gray-900 sm:text-right">
                    {price}
                  </p>
                ) : null}
              </div>
            </li>
          );
        })}
        {moreCount > 0 ? (
          <li className={`${pad} bg-gray-50/50`}>
            <p className="text-xs font-semibold text-gray-500">
              +{moreCount} more {moreCount === 1 ? "class" : "classes"}
            </p>
          </li>
        ) : null}
      </ul>
    </div>
  );
}
